import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { Warehouse, WarehouseCountry } from '@/domain';
import { warehouseService } from '@/services/warehouseService';
import { mockStore } from '@/lib/mock-store';
import { isMockBackend } from '@/lib/supabase';

export type AppRole = 'PLANNER' | 'OPS' | 'ADMIN' | 'AUDITOR';

export interface CountryDef {
  code: WarehouseCountry;
  name: string;
}

export const COUNTRIES: CountryDef[] = [
  { code: 'VN', name: 'Việt Nam' },
  { code: 'TH', name: 'Thái Lan' },
  { code: 'PH', name: 'Philippines' },
];

interface ScopeState {
  country: WarehouseCountry;
  setCountry: (c: WarehouseCountry) => void;
  warehouseId: string;
  setWarehouseId: (id: string) => void;
  role: AppRole;
  setRole: (r: AppRole) => void;
  warehouses: Warehouse[];
  countryWarehouses: Warehouse[];
  currentWarehouse: Warehouse | null;
}

const ScopeContext = createContext<ScopeState | null>(null);

const STORAGE_KEY = 'wfp-v2:scope:v1';

interface Stored {
  country: WarehouseCountry;
  warehouseId: string;
  role: AppRole;
}

function loadStored(): Partial<Stored> {
  if (typeof window === 'undefined') return {};
  try {
    return JSON.parse(window.localStorage.getItem(STORAGE_KEY) ?? '{}') as Partial<Stored>;
  } catch {
    return {};
  }
}

export function ScopeProvider({ children }: { children: ReactNode }) {
  const stored = loadStored();
  const [country, setCountryRaw] = useState<WarehouseCountry>(stored.country ?? 'VN');
  const [warehouseId, setWarehouseIdRaw] = useState<string>(stored.warehouseId ?? '');
  const [role, setRoleRaw] = useState<AppRole>(stored.role ?? 'PLANNER');
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);

  useEffect(() => {
    let alive = true;
    function refresh() {
      warehouseService.list().then((rows) => {
        if (alive) setWarehouses(rows);
      });
    }
    refresh();
    if (isMockBackend) return mockStore.subscribe(refresh);
    return () => {
      alive = false;
    };
  }, []);

  // Auto-pick first warehouse if none stored or stored warehouse is not in current country
  useEffect(() => {
    if (warehouses.length === 0) return;
    const stillValid = warehouses.find(
      (w) => w.id === warehouseId && w.country === country && w.is_active,
    );
    if (stillValid) return;
    const first = warehouses.find((w) => w.country === country && w.is_active);
    if (first) setWarehouseIdRaw(first.id);
  }, [warehouses, country, warehouseId]);

  // Persist
  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ country, warehouseId, role } satisfies Stored),
    );
  }, [country, warehouseId, role]);

  const value = useMemo<ScopeState>(() => {
    const countryWarehouses = warehouses.filter((w) => w.country === country);
    const currentWarehouse = warehouses.find((w) => w.id === warehouseId) ?? null;
    return {
      country,
      setCountry: setCountryRaw,
      warehouseId,
      setWarehouseId: setWarehouseIdRaw,
      role,
      setRole: setRoleRaw,
      warehouses,
      countryWarehouses,
      currentWarehouse,
    };
  }, [country, warehouseId, role, warehouses]);

  return <ScopeContext.Provider value={value}>{children}</ScopeContext.Provider>;
}

export function useScope(): ScopeState {
  const ctx = useContext(ScopeContext);
  if (!ctx) throw new Error('useScope must be used inside <ScopeProvider>');
  return ctx;
}

export const ROLE_OPTIONS: { value: AppRole; label: string }[] = [
  { value: 'PLANNER', label: 'Lập kế hoạch' },
  { value: 'OPS', label: 'Quản lý vận hành' },
  { value: 'ADMIN', label: 'Quản trị' },
  { value: 'AUDITOR', label: 'Kiểm toán' },
];
